import classNames from 'classnames';
import React, { ChangeEvent, Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import isFunction from 'lodash/isFunction';
import { useTranslation } from 'react-i18next';

import { UiCleanDropdownProps } from 'src/components/UI/dropdown/types';
import { CSS_ANIMATION_DURATION } from 'src/config';
import { BaseOptionType } from 'src/utils/commonForm';
import { useCleanHoverErrors } from 'src/components/UI/inputs/useHoverErrors';
import { UiHoverTooltip } from 'src/components/UI/HoverTooltip';
import { useInputCleanBaseHook } from 'src/utils/hooks/inputs';
import { useRefClickOutside } from 'src/utils/hooks/useRefClickOutside';
import { StateFormInputOptionsType } from 'src/utils/stateForm';
import { getTestId, TestIdAddType } from 'src/utils/testing';
import { DropdownChevronIcon } from 'src/components/UI/dropdown/Chevron';
import { MaterialErrorMessage } from 'src/components/UI/common/MaterialErrorMessage';
import { UiTextTooltip } from 'src/components/UI/TextTooltip';

// <dropdown> has to be declared because of css
declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      dropdown: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
    }
  }
}

export const EMPTY_SELECT_VALUE = null; // empty value for other option

/**
 * simple dropdown component (select)
 */
export const UiCleanDropdown: React.FC<UiCleanDropdownProps> = memo(
  ({
    formProps: { clearErrors, onChange: formOnChange, register, unregister, getSubscribeProps },
    items,
    name,
    placeholder,
    required = false,
    disabled = false,
    onChange,
    optionsCaption,
    hiddenItems,
    dataTestId,
    allowNullableValue = false,
    showHoverError,
    errorLabel,
    requiredMessage,
    className,
    material,
    validateName,
    loading,
    optionClassName,
    showTooltip = false,
    searchEnabled,
    prefix,
    showTitle,
    categories,
  }: UiCleanDropdownProps) => {
    const { t } = useTranslation();

    const inputRef = useRef<HTMLInputElement | null>(null);

    const [optionsListMargin, setOptionsListMargin] = useState(0);

    const [isActive, setIsActive] = useState(false);

    const [searchInputValue, setSearchInputValue] = useState('');

    const [paddingLeft, setPaddingLeft] = useState<number | undefined>();

    const inputOptions = useMemo(
      (): StateFormInputOptionsType => ({
        required: required || false,
        requiredMessage,
        errorLabel,
      }),
      [errorLabel, required, requiredMessage]
    );

    const [value, errors] = useInputCleanBaseHook<string | number | null | undefined>({
      getSubscribeProps,
      name,
      inputOptions,
      unregister,
      register,
      type: 'dropdown',
      validateName,
    });
    const hasErrors = !!errors?.length;

    const [show, setShow] = useState(false);
    const [render, setRender] = useState(false);

    const allowSearchInputCaption = !searchEnabled || (searchEnabled && !isActive && !show);

    const selected = useMemo(() => {
      if ([undefined, null].includes(value as undefined | null)) {
        if (allowSearchInputCaption && allowNullableValue && (optionsCaption || placeholder)) {
          return {
            label: optionsCaption || placeholder,
            value: undefined,
          };
        }

        if (!allowNullableValue) {
          return null;
        }
      }

      return items.find((item) => item.value === value) || null;
    }, [allowNullableValue, allowSearchInputCaption, items, optionsCaption, placeholder, value]);

    useEffect(() => {
      setSearchInputValue(selected?.label || '');
    }, [selected?.label]);

    const ref = useRefClickOutside<HTMLDivElement>(show, () => setShow(false));

    const getNoHiddenItems = (
      isHidden: UiCleanDropdownProps['hiddenItems'],
      dropdownItems: UiCleanDropdownProps['items']
    ) => (isHidden ? dropdownItems.filter((item) => !isHidden.includes(item.value)) : dropdownItems);

    const [options, setOptions] = useState<UiCleanDropdownProps['items']>(() => getNoHiddenItems(hiddenItems, items));

    useEffect(() => {
      setOptions(getNoHiddenItems(hiddenItems, items));
    }, [hiddenItems, items]);

    const hasPlaceholder = !!placeholder;

    /** scroll to selected option manually */
    const scrollToSelectedOption = useCallback(() => {
      const selectedIndex = options.findIndex((item) => item.value === value);
      const ul = ref.current?.querySelector('ul');

      if (ul && selectedIndex !== -1) {
        const selectedElement: HTMLLIElement | null = ul.querySelector(`li:nth-of-type(${selectedIndex + 1})`);

        if (selectedElement) {
          const paddingTopOption = material && !hasPlaceholder ? 14 : 0;
          ul.scrollTop = selectedElement.offsetTop - paddingTopOption;
        }
      }
    }, [hasPlaceholder, material, options, ref, value]);

    /** track a click on outer elements when dropdown is opened */
    const hasEventListener = useRef(false);

    useEffect(() => {
      if (show) {
        hasEventListener.current = true;
      } else if (hasEventListener) {
        hasEventListener.current = false;
        setTimeout(() => {
          setRender(false);
        }, CSS_ANIMATION_DURATION / 2);
      }
    }, [show]);
    /** end track a click on outer elements... */

    const onOpenOptionsList = useCallback(() => {
      if (!disabled) {
        if (searchEnabled && inputRef.current) {
          inputRef.current.focus();

          const INPUT_MARGIN = 5;
          setOptionsListMargin(inputRef.current.offsetHeight + INPUT_MARGIN);
        }

        setRender(true);

        /** render just a little later so the animation will be correct */
        setTimeout(() => {
          scrollToSelectedOption();

          setShow(true);
        }, 0);
      }
    }, [disabled, scrollToSelectedOption, searchEnabled]);

    const { errorString, onChangeInputHover } = useCleanHoverErrors({
      name,
      errors,
      clearErrors,
      showAnyError: showHoverError || material,
      validateName,
    });

    const onSelect = useCallback(
      (value: BaseOptionType<typeof EMPTY_SELECT_VALUE> | undefined) => () => {
        setShow(false);

        onChangeInputHover();

        /** don't allow to select an empty value if allowNullableValue is false  */
        if (value === undefined && !allowNullableValue) {
          return;
        }

        const valueToDispatch = value ? value.value : undefined;

        formOnChange(name, valueToDispatch);

        if (isFunction(onChange)) {
          onChange(valueToDispatch);
        }
      },
      [onChangeInputHover, allowNullableValue, formOnChange, name, onChange]
    );

    const [inputTestId, optionTestId]: [string | undefined, TestIdAddType] = useMemo(() => {
      if (!dataTestId) {
        return [undefined, () => undefined];
      }
      const id = getTestId('dropdown', dataTestId, name);

      return [id(), id];
    }, [dataTestId, name]);

    const filterOptions = useCallback(
      (text: string) => {
        const actualItems = getNoHiddenItems(hiddenItems, items);

        if (!text) {
          setOptions(actualItems);
        } else {
          setOptions(actualItems.filter((item) => item.label.toLowerCase().trim().includes(text.toLowerCase().trim())));
        }
      },
      [hiddenItems, items]
    );

    const handleChangeSearchInput = (event: ChangeEvent<HTMLInputElement>) => {
      setSearchInputValue(event.target.value);
      filterOptions(event.target.value);
    };

    useEffect(() => {
      /** reset the search input value to the last selected, when the list of options are closed */
      if (searchEnabled && !render && inputRef.current) {
        if (selected?.value) {
          setSearchInputValue(selected.label);
        } else {
          setSearchInputValue(optionsCaption || placeholder || '');
        }

        filterOptions('');
      }
    }, [filterOptions, searchEnabled, selected?.label, selected?.value, render, optionsCaption, placeholder]);

    const isPressedTabBtnRef = useRef(false);

    const prefixRef = useRef<HTMLSpanElement | null>(null);

    useEffect(() => {
      const element = prefixRef.current;

      if (element && prefix) {
        const prefixWidth = parseInt(window.getComputedStyle(element).getPropertyValue('width'), 10);

        setPaddingLeft(prefixWidth + 1);
      } else {
        setPaddingLeft(undefined);
      }
    }, [prefix]);

    const optionsWithCategory = useMemo(
      () =>
        categories
          ? categories.map(({ component, values, id }) => ({
              id,
              component,
              options: options.filter((item) => values.includes(item.value)),
            }))
          : null,
      [categories, options]
    );

    if (material) {
      return (
        <div className={classNames('dropdown-material custom', className)}>
          <dropdown className="custom-dropdown">
            <div className="aex-dropdown-wrapper" ref={ref}>
              {render && (
                <div className="aex-dropdown-list-wrapper">
                  {placeholder && !searchEnabled && (
                    <div className="dropdown-list-placeholder">
                      <span role="presentation" onClick={() => setShow(false)}>
                        {placeholder}
                      </span>
                      <div role="presentation" className="aex-dropdown-caret" onClick={() => setShow(false)}>
                        <DropdownChevronIcon />
                      </div>
                    </div>
                  )}
                  <ul
                    className={classNames('aex-dropdown-list', {
                      anim: show,
                      withPlaceholder: hasPlaceholder && !searchEnabled,
                    })}
                    style={searchEnabled ? { marginTop: optionsListMargin } : undefined}
                  >
                    {optionsCaption && (
                      <li
                        role="presentation"
                        className={classNames('aex-dropdown-option caption', optionClassName, {
                          selected: [undefined, null].includes(value as undefined | null),
                        })}
                        onClick={onSelect(undefined)}
                        data-testid={optionTestId('caption')}
                        title={showTitle ? optionsCaption : undefined}
                      >
                        {optionsCaption}
                      </li>
                    )}

                    {optionsWithCategory ? (
                      <>
                        {optionsWithCategory.map(({ component, options, id }) => (
                          <Fragment key={id}>
                            {component}

                            {options.map((item) => (
                              <li
                                key={item.value}
                                role="presentation"
                                className={classNames('aex-dropdown-option', optionClassName, {
                                  selected: item.value === value,
                                })}
                                onClick={onSelect(item)}
                                data-testid={optionTestId(item.value)}
                                title={showTitle ? item.label : undefined}
                              >
                                {showTooltip ? (
                                  <UiTextTooltip text={item.label}>{item.label}</UiTextTooltip>
                                ) : (
                                  item.label
                                )}
                              </li>
                            ))}
                          </Fragment>
                        ))}
                      </>
                    ) : (
                      options.map((item) => (
                        <li
                          key={item.value}
                          role="presentation"
                          className={classNames('aex-dropdown-option', optionClassName, {
                            selected: item.value === value,
                          })}
                          onClick={onSelect(item)}
                          data-testid={optionTestId(item.value)}
                          title={showTitle ? item.label : undefined}
                        >
                          {showTooltip ? <UiTextTooltip text={item.label}>{item.label}</UiTextTooltip> : item.label}
                        </li>
                      ))
                    )}

                    {searchEnabled && !!searchInputValue && !options.length && (
                      <li
                        key="noOptions"
                        className={classNames('aex-dropdown-option no-options', optionClassName)}
                        data-testid={optionTestId('noOptions')}
                      >
                        {t('common.ui.noOptions')}
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {!!prefix && (
                <span key="prefix" className="prefix" ref={prefixRef}>
                  {prefix}
                </span>
              )}

              {searchEnabled ? (
                <input
                  type="text"
                  className={classNames('aex-dropdown-input', {
                    isSearch: true,
                    required: hasErrors && required,
                    error: hasErrors,
                    disabled,
                    noFocus: render,
                  })}
                  onClick={onOpenOptionsList}
                  value={searchInputValue}
                  onChange={handleChangeSearchInput}
                  placeholder={loading ? t('common.loadingDots') : placeholder}
                  data-testid={inputTestId}
                  data-name={name}
                  disabled={disabled}
                  ref={inputRef}
                  onFocus={() => {
                    setIsActive(true);
                    onOpenOptionsList();
                  }}
                  onKeyDown={(event) => {
                    isPressedTabBtnRef.current = event.code === 'Tab';
                  }}
                  onBlur={() => {
                    setIsActive(false);

                    if (isPressedTabBtnRef.current) {
                      isPressedTabBtnRef.current = false;
                      setShow(false);
                    }
                  }}
                  title={showTitle ? (!isActive && searchInputValue) || undefined : undefined}
                />
              ) : (
                <input
                  type="text"
                  className={classNames('aex-dropdown-input', {
                    required: hasErrors && required,
                    error: hasErrors,
                    disabled,
                    noFocus: render,
                  })}
                  readOnly
                  onClick={onOpenOptionsList}
                  value={selected?.label}
                  placeholder={loading ? t('common.loadingDots') : placeholder}
                  data-testid={inputTestId}
                  data-name={name}
                  disabled={disabled}
                  style={{ paddingLeft }}
                  title={showTitle ? selected?.label : undefined}
                />
              )}

              <div
                role="presentation"
                className={classNames('aex-dropdown-caret', {
                  disabled,
                })}
                onClick={onOpenOptionsList}
              >
                <DropdownChevronIcon />
              </div>
            </div>
          </dropdown>
          <MaterialErrorMessage error={errorString} />
        </div>
      );
    }

    return (
      <dropdown className={classNames('custom-dropdown', className)}>
        <UiHoverTooltip id={name} text={errorString} render={!!errorString} tooltipClassName="error-tooltip" noWrapper>
          <div className="aex-dropdown-wrapper" ref={ref}>
            {render && (
              <div className="aex-dropdown-list-wrapper">
                <ul
                  className={classNames('aex-dropdown-list', {
                    anim: show,
                  })}
                  style={searchEnabled ? { marginTop: optionsListMargin } : undefined}
                >
                  {optionsCaption && (
                    <li
                      role="presentation"
                      className={classNames('aex-dropdown-option caption', optionClassName, {
                        selected: [undefined, null].includes(value as undefined | null),
                      })}
                      onClick={onSelect(undefined)}
                      data-testid={optionTestId('caption')}
                      title={showTitle ? optionsCaption : undefined}
                    >
                      {optionsCaption}
                    </li>
                  )}
                  {options.map((item) => (
                    <li
                      key={item.value}
                      role="presentation"
                      className={classNames('aex-dropdown-option', optionClassName, {
                        selected: item.value === value,
                      })}
                      onClick={onSelect(item)}
                      data-testid={optionTestId(item.value)}
                      title={showTitle ? item.label : undefined}
                    >
                      {item.label}
                    </li>
                  ))}

                  {searchEnabled && !!searchInputValue && !options.length && (
                    <li
                      key="noOptions"
                      className={classNames('aex-dropdown-option no-options', optionClassName)}
                      data-testid={optionTestId('noOptions')}
                    >
                      {t('common.ui.noOptions')}
                    </li>
                  )}
                </ul>
              </div>
            )}

            {searchEnabled ? (
              <input
                type="text"
                className={classNames('aex-dropdown-input', {
                  isSearch: true,
                  required: hasErrors && required,
                  error: hasErrors,
                  disabled,
                })}
                onClick={onOpenOptionsList}
                value={searchInputValue}
                onChange={handleChangeSearchInput}
                placeholder={placeholder}
                data-testid={inputTestId}
                data-name={name}
                disabled={disabled}
                ref={inputRef}
                onFocus={() => setIsActive(true)}
                onBlur={() => setIsActive(false)}
                title={showTitle ? (!isActive && searchInputValue) || undefined : undefined}
              />
            ) : (
              <input
                type="text"
                className={classNames('aex-dropdown-input', {
                  required: hasErrors && required,
                  error: hasErrors,
                  disabled,
                })}
                readOnly
                onClick={onOpenOptionsList}
                value={selected?.label}
                placeholder={placeholder}
                data-testid={inputTestId}
                data-name={name}
                disabled={disabled}
                title={showTitle ? selected?.label : undefined}
              />
            )}
            <div
              role="presentation"
              className={classNames('aex-dropdown-caret material-icons', {
                disabled,
              })}
              onClick={onOpenOptionsList}
            >
              expand_more
            </div>
          </div>
        </UiHoverTooltip>
      </dropdown>
    );
  }
);
